import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { CurrencyPipe } from '@angular/common';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { DateFormatDirective, FULL_FORMAT } from './util/directives/date-format.directive';
import * as Sentry from "@sentry/angular";
import { Router } from '@angular/router';

import { MAT_SELECT_CONFIG, MatSelectModule } from '@angular/material/select';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatDialogModule } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {MatIconModule } from '@angular/material/icon';
import {MatRadioModule} from '@angular/material/radio';
import {MatChipsModule} from '@angular/material/chips';
import {MatTableModule} from '@angular/material/table';
import {MatSortModule} from '@angular/material/sort';
import {MatPaginatorModule} from '@angular/material/paginator';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatExpansionModule} from '@angular/material/expansion';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { StartComponent } from './pages/start/start.component';
import { TrustComponent } from './pages/trust/trust.component';
import { EstateComponent } from './pages/estate/estate.component';
import { InputComponent } from './components/input/input.component';
import { OtpPopupComponent } from './components/otp-popup/otp-popup.component';
import { FormBottomComponent } from './components/form-bottom/form-bottom.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RadiosComponent } from './components/radios/radios.component';
import { IntlTelInputNgModule } from  'intl-tel-input-ng';
import { ToastrModule } from 'ngx-toastr';

import { Step1BasicComponent } from './pages/trust/step1-basic/step1-basic.component';
import { Step2SettlorComponent } from './pages/trust/step2-settlor/step2-settlor.component';
import { Step3BeneficiaryComponent } from './pages/trust/step3-beneficiary/step3-beneficiary.component';
import { Step4GuardianComponent } from './pages/trust/step4-guardian/step4-guardian.component';
import { Step5TrustInfoComponent } from './pages/trust/step5-trust-info/step5-trust-info.component';
import { Step6PreviewComponent } from './pages/trust/step6-preview/step6-preview.component';
import { Step7PaymentComponent } from './pages/trust/step7-payment/step7-payment.component';
import { Step8CompleteComponent } from './pages/trust/step8-complete/step8-complete.component';

import { TrustFundComponent } from './pages/info/trust-fund/trust-fund.component';
import { EstatePlanningComponent } from './pages/info/estate-planning/estate-planning.component';
import { BookCallComponent } from './components/book-call/book-call.component';

import { NgOtpInputModule } from 'ng-otp-input';
import { NgxMatIntlTelInputComponent } from 'ngx-mat-intl-tel-input';
import { FileValueAccessorDirective } from './util/directives/file-value-accessor.directive';
import { FileValidatorDirective } from './util/directives/file-validator.directive';
import { ApiService } from './services/api/api.service';
import { RecordEditorComponent } from './components/record-editor/record-editor.component';

import { AssignGuardianComponent } from './components/assign-guardian/assign-guardian.component';
import { Step1SettlorComponent } from './pages/estate/appointments/step1-settlor/step1-settlor.component';
import { Step2AssetsComponent } from './pages/estate/appointments/step2-assets/step2-assets.component';
import { Step3RefereeComponent } from './pages/estate/appointments/step3-referree/step3-referee.component';
import { Step4ScheduleComponent } from './pages/estate/appointments/step4-schedule/step4-schedule.component';
import { ContactInfoComponent } from './pages/estate/appointments/step1-contact-info/contact-info.component';
import { StepSummaryComponent } from './pages/estate/appointments/step-summary/step-summary.component';
import { AppointmentsComponent } from './pages/estate/appointments/appointments.component';

import { OptionPopupComponent } from './pages/estate/option-popup/option-popup.component';

import { Step1SettlorsComponent } from './pages/estate/private-trust/step1-settlors/step1-settlors.component';
import { Step2PaymentEstateComponent } from './pages/estate/private-trust/step2-payment-estate/step2-payment-estate.component';
import { Step2PaymentWillComponent } from './pages/estate/will-creation/step2-payment-will/step2-payment-will.component';
import { Step3BeneficiaryEstateComponent } from './pages/estate/private-trust/step3-beneficiary-estate/step3-beneficiary-estate.component';
import { Step4AssetsComponent } from './pages/estate/private-trust/step4-assets/step4-assets.component';
import { Step5TrusteeComponent } from './pages/estate/private-trust/step5-trustee/step5-trustee.component';
import { Step6EnforcerComponent } from './pages/estate/private-trust/step6-enforcer/step6-enforcer.component';

import { Step1TestatorComponent } from './pages/estate/will-creation/step1-testator/step1-testator.component';
import { Step3ExecutorComponent } from './pages/estate/will-creation/step3-executor/step3-executor.component';
import { Step6WitnessComponent } from './pages/estate/will-creation/step6-witness/step6-witness.component';

import { Step7PreviewsComponent } from './pages/estate/private-trust/step7-previews/step7-previews.component';

import { AssetTablesComponent } from './components/asset-tables/asset-tables.component';
import { LegalConsultationComponent } from './pages/estate/legal-consultation/legal-consultation.component';
import { EstateEditorComponent } from './components/estate-editor/estate-editor.component';
import { ModalComponent } from './components/modal/modal.component';
import { ResourcePipe } from './pipes/resource.pipe';
import { FormSectionComponent } from './components/form-section/form-section.component';
import { ChecklistComponent } from './util/PDF/checklist/checklist.component';
import { GrantLetterComponent } from './util/PDF/grant-letter/grant-letter.component';
import { SignatureComponent } from './components/signature/signature.component';
import { AjuaService } from './services/ajua/ajua.service';
import { SectionComponent } from './components/section/section.component';
import { RegionComponent } from './pages/region/region.component';
import { SetupComponent } from './pages/estate/setup/setup.component';
import { SharesComponent } from './components/shares/shares.component';
import { CardsComponent } from './components/cards/cards.component';
import { FormGridComponent } from './components/form-grid/form-grid.component';
import { MedicalComponent } from './pages/info/medical/medical.component';


function initializeAjuaFeedback(feedbackService: AjuaService) {
  return () => {
    // Define routes where feedback should be available
    const feedbackRoutes = ['/milele-trust/success', '/estate-planning/success', '/estate-planning/appointments/success'];
    feedbackService.initialize(feedbackRoutes);
  };
}

@NgModule({
  declarations: [
    AppComponent,
    StartComponent,
    Step1BasicComponent,
    TrustComponent,
    InputComponent,
    OtpPopupComponent,
    FormBottomComponent,
    Step2SettlorComponent,
    Step3BeneficiaryComponent,
    Step4GuardianComponent,
    RadiosComponent,
    Step5TrustInfoComponent,
    Step6PreviewComponent,
    Step7PaymentComponent,
    FileValueAccessorDirective,
    FileValidatorDirective,
    RecordEditorComponent,
    DateFormatDirective,
    Step8CompleteComponent,
    TrustFundComponent,
    EstatePlanningComponent,
    BookCallComponent,
    AssignGuardianComponent,
    EstateComponent,
    Step1SettlorComponent,
    Step2AssetsComponent,
    Step3RefereeComponent,
    Step4ScheduleComponent,
    ContactInfoComponent,
    StepSummaryComponent,
    AppointmentsComponent,
    OptionPopupComponent,
    Step1SettlorsComponent,
    Step3BeneficiaryEstateComponent,
    Step4AssetsComponent,
    Step5TrusteeComponent,
    Step6EnforcerComponent,
    Step7PreviewsComponent,
    Step2PaymentEstateComponent,
    Step1TestatorComponent,
    Step3ExecutorComponent,
    Step6WitnessComponent,
    Step2PaymentWillComponent,
    AssetTablesComponent,
    LegalConsultationComponent,
    EstateEditorComponent,
    ModalComponent,
    ResourcePipe,
    FormSectionComponent,
    ChecklistComponent,
    GrantLetterComponent,
    SignatureComponent,
    SectionComponent,
    RegionComponent,
    SetupComponent,
    SharesComponent,
    CardsComponent,
    FormGridComponent,
    MedicalComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MatSelectModule,
    MatInputModule,
    MatDialogModule,
    MatButtonModule,
    MatDatepickerModule,
    ReactiveFormsModule,
    FormsModule,
    MatCheckboxModule,
    MatIconModule,
    MatRadioModule,
    NgOtpInputModule,
    MatNativeDateModule,
    NgxMatIntlTelInputComponent,
    MatProgressSpinnerModule,
    MatSortModule,
    MatChipsModule,
    MatTableModule,
    MatPaginatorModule,
    MatAutocompleteModule,
    MatSlideToggleModule,
    MatExpansionModule,
    IntlTelInputNgModule.forRoot(),
    ToastrModule.forRoot({
      maxOpened: 3,
      autoDismiss: true,
      preventDuplicates: true,
      countDuplicates: true,
      resetTimeoutOnDuplicate: true,
    })
  ],
  providers: [
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { overlayPanelClass: 'width-overlay' }
    },
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
    { provide: MAT_DATE_FORMATS, useValue: FULL_FORMAT },
    { provide: LOCALE_ID, useValue: 'en-US' },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    CurrencyPipe,
    ApiService,
    AjuaService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAjuaFeedback,
      deps: [AjuaService],
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor() {}
}
